import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  DateField,
  Toolbar,
  SaveButton,
  DeleteButton,
  usePermissions,
  useRecordContext,
} from 'react-admin';
import { Box, Card, CardContent, Typography, Chip, Grid } from '@mui/material';

const entityTypeChoices = [
  { id: 'cricket_player', name: 'Cricket Player' },
  { id: 'movie', name: 'Movie' },
  { id: 'movie_song', name: 'Movie Song' },
  { id: 'city', name: 'City' },
  { id: 'company', name: 'Company' },
];

const validateJson = (value: unknown) => {
  if (typeof value === 'string') {
    try {
      JSON.parse(value);
    } catch {
      return 'Invalid JSON';
    }
  }
  return undefined;
};

// JSON input component using textarea
const JsonInput = ({ source, label }: { source: string; label: string }) => {
  return (
    <TextInput
      source={source}
      label={label}
      multiline
      fullWidth
      minRows={10}
      maxRows={30}
      validate={validateJson}
      format={(value: unknown) =>
        typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value || '{}')
      }
      parse={(value: string) => {
        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      }}
      sx={{
        '& .MuiInputBase-input': {
          fontFamily: 'monospace',
          fontSize: '0.875rem',
        },
      }}
    />
  );
};

const VerificationStatus = () => {
  const record = useRecordContext();
  if (!record) return null;

  const status = record.verification_status || 'unverified';
  const colorMap: Record<string, 'default' | 'warning' | 'success'> = {
    unverified: 'default',
    pending_review: 'warning',
    verified: 'success',
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Verification Status
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Chip
            label={status.replace('_', ' ')}
            color={colorMap[status] || 'default'}
          />
          {record.verified_at && (
            <Typography variant="body2" color="textSecondary">
              Verified: {new Date(record.verified_at).toLocaleString()}
            </Typography>
          )}
        </Box>
        {record.verification_notes && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Notes: {record.verification_notes}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const EditToolbar = () => {
  const { permissions } = usePermissions();
  const canDelete =
    permissions?.role === 'super_admin' ||
    permissions?.permissions?.includes('entities:delete');

  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <SaveButton />
      {canDelete && <DeleteButton />}
    </Toolbar>
  );
};

export const EntityEdit = () => {
  return (
    <Edit>
      <SimpleForm toolbar={<EditToolbar />} warnWhenUnsavedChanges>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextInput source="display_name" fullWidth required />
            <SelectInput
              source="entity_type"
              choices={entityTypeChoices}
              fullWidth
              required
            />
            <TextInput
              source="search_tokens"
              fullWidth
              format={(value: string[]) => (Array.isArray(value) ? value.join(', ') : '')}
              parse={(value: string) =>
                value
                  .split(',')
                  .map((s) => s.trim().toLowerCase())
                  .filter(Boolean)
              }
              helperText="Comma-separated search terms"
            />
            <JsonInput source="attributes" label="Attributes (JSON)" />
          </Grid>
          <Grid item xs={12} md={4}>
            <VerificationStatus />
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Metadata
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>ID:</strong>
                </Typography>
                <TextInput source="id" disabled fullWidth />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  <strong>Created:</strong>
                </Typography>
                <DateField source="created_at" showTime />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  <strong>Updated:</strong>
                </Typography>
                <DateField source="updated_at" showTime />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </SimpleForm>
    </Edit>
  );
};
